import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Play, RefreshCw, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Monitoring() {
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [sitemapEntries, setSitemapEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]); // Toutes les entrées non filtrées
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState("all");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [keywords, setKeywords] = useState("");
  const [wordpressSites, setWordpressSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");

  useEffect(() => {
    fetchSources();
    fetchUnprocessedEntries();
    fetchWordPressSites();
  }, []);

  useEffect(() => {
    filterEntries();
  }, [selectedSource, allEntries]);

  const fetchSources = async () => {
    try {
      const response = await axios.get(`${API}/sources`);
      setSources(response.data);
    } catch (error) {
      console.error("Erreur chargement sources:", error);
    }
  };

  const fetchWordPressSites = async () => {
    try {
      const response = await axios.get(`${API}/wordpress/sites`);
      setWordpressSites(response.data);
      if (response.data.length > 0) {
        setSelectedSite(response.data[0].id);
      }
    } catch (error) {
      console.error("Erreur chargement sites WordPress:", error);
    }
  };

  const fetchUnprocessedEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sitemap-entries?unprocessed=true&limit=10000`);
      setAllEntries(response.data);
      setSitemapEntries(response.data);
    } catch (error) {
      console.error("Erreur chargement entrées:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterEntries = () => {
    if (selectedSource === "all") {
      setSitemapEntries(allEntries);
    } else {
      const filtered = allEntries.filter(entry => entry.source_id === selectedSource);
      setSitemapEntries(filtered);
    }
  };

  const runMonitoring = async () => {
    setRunning(true);
    setResult(null);
    
    try {
      const response = await axios.post(`${API}/monitoring/run`);
      setResult(response.data);
      
      if (response.data.new_urls_found > 0) {
        toast.success(`${response.data.new_urls_found} nouvelles URLs détectées !`);
        fetchUnprocessedEntries();
      } else {
        toast.info("Aucune nouvelle URL détectée");
      }
    } catch (error) {
      console.error("Erreur monitoring:", error);
      toast.error("Erreur lors de la veille");
    } finally {
      setRunning(false);
    }
  };

  // Ouvrir le modal AVANT toute génération (pour choisir le site WP)
  const openGenerateModal = (entry) => {
    if (wordpressSites.length === 0) {
      toast.error("⚠️ Aucun site WordPress configuré. Allez dans Paramètres pour en ajouter un.");
      return;
    }
    setSelectedEntry(entry);
    setShowKeywordsModal(true);
  };

  const handleGenerateArticle = async () => {
    if (!selectedEntry) return;
    
    if (!selectedSite) {
      toast.error("Veuillez sélectionner un site WordPress de destination");
      return;
    }
    
    const url = selectedEntry.url;
    const entryId = selectedEntry.id;
    
    setGenerating(entryId);
    setShowKeywordsModal(false);
    
    try {
      toast.info("Génération en cours... (30-60 secondes)");
      
      const keywordsList = keywords 
        ? keywords.split(',').map(k => k.trim()).filter(k => k)
        : [];
      
      const response = await axios.post(`${API}/articles/generate`, {
        source_url: url,
        keywords: keywordsList,
        wordpress_site_id: selectedSite
      });
      
      toast.success("Article généré avec succès !");
      
      // Rediriger vers la page Articles après 1 seconde
      setTimeout(() => navigate("/articles"), 1000);
    } catch (error) {
      console.error("Erreur génération:", error);
      
      // Gérer les erreurs de validation ou autres
      let errorMessage = "Erreur lors de la génération";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Si c'est un objet (erreur de validation), convertir en string
        if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        } else {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setGenerating(null);
      setKeywords("");
      setSelectedEntry(null);
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="monitoring-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Veille automatique</h1>
          <p className="text-gray-600">Détectez les nouvelles publications sur vos sources</p>
        </div>
        <button
          onClick={runMonitoring}
          disabled={running}
          className="btn-primary flex items-center space-x-2 disabled:opacity-50"
          data-testid="run-monitoring-button"
        >
          {running ? (
            <>
              <RefreshCw size={20} className="animate-spin" />
              <span>Scan en cours...</span>
            </>
          ) : (
            <>
              <Play size={20} />
              <span>Lancer la veille</span>
            </>
          )}
        </button>
      </div>

      {/* Result Card */}
      {result && (
        <div className="bg-gradient-to-r from-[#4F86EF] to-[#3A6B35] rounded-2xl p-6 text-white fade-in" data-testid="monitoring-result">
          <h3 className="text-xl font-bold mb-4">Résultat de la veille</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-3xl font-bold">{result.sources_checked}</div>
              <div className="text-white/80 text-sm">Sources scannées</div>
            </div>
            <div>
              <div className="text-3xl font-bold">{result.new_urls_found}</div>
              <div className="text-white/80 text-sm">Nouvelles URLs</div>
            </div>
            <div>
              <div className="text-xs text-white/80">
                {new Date(result.timestamp).toLocaleString('fr-FR')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtre par source */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filtrer par source
        </label>
        <select
          value={selectedSource}
          onChange={(e) => setSelectedSource(e.target.value)}
          className="w-full md:w-64 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
        >
          <option value="all">Toutes les sources ({allEntries.length})</option>
          {sources.map((source) => {
            const count = allEntries.filter(e => e.source_id === source.id).length;
            return (
              <option key={source.id} value={source.id}>
                {source.name} ({count})
              </option>
            );
          })}
        </select>
      </div>

      {/* Unprocessed URLs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">URLs détectées à traiter</h2>
          <button
            onClick={fetchUnprocessedEntries}
            className="btn-secondary flex items-center space-x-2"
            data-testid="refresh-entries-button"
          >
            <RefreshCw size={16} />
            <span>Actualiser</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="loading-spinner"></div>
          </div>
        ) : sitemapEntries.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center" data-testid="no-entries">
            <p className="text-gray-600">Aucune URL à traiter</p>
            <p className="text-sm text-gray-400 mt-2">Lancez une veille pour détecter de nouvelles publications</p>
          </div>
        ) : (
          <div className="table-container" data-testid="entries-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-header text-left">URL</th>
                    <th className="table-header text-left">Détectée le</th>
                    <th className="table-header text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sitemapEntries.map((entry) => (
                    <tr key={entry.id} className="table-row" data-testid={`entry-${entry.id}`}>
                      <td className="px-6 py-4">
                        <a
                          href={entry.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#4F86EF] hover:underline flex items-center space-x-2"
                        >
                          <span className="truncate max-w-xl">{entry.url}</span>
                          <ExternalLink size={14} />
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(entry.detected_at).toLocaleString('fr-FR')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openGenerateModal(entry)}
                            disabled={generating === entry.id}
                            className="btn-primary text-sm flex items-center space-x-1 disabled:opacity-50"
                            data-testid={`generate-from-${entry.id}`}
                          >
                            {generating === entry.id ? (
                              <>
                                <div className="loading-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></div>
                                <span>Génération...</span>
                              </>
                            ) : (
                              <>
                                <Sparkles size={16} />
                                <span>Générer</span>
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-2">ℹ️ Comment fonctionne la veille ?</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li>• Le système scanne les sitemaps de vos sources actives</li>
          <li>• Il détecte automatiquement les nouvelles URLs publiées</li>
          <li>• Cliquez sur "Générer" pour créer un article IA instantanément</li>
          <li>• Ou cliquez sur "+ Mots-clés" pour personnaliser les mots-clés SEO</li>
          <li>• Configurez un cron job pour automatiser la veille quotidienne</li>
        </ul>
      </div>

      {/* Modal de génération avec sélection du site */}
      {showKeywordsModal && selectedEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="generate-modal">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">🚀 Générer un article</h2>
            <p className="text-sm text-gray-600 mb-4">
              <span className="font-medium">Source :</span> {selectedEntry.url.substring(0, 60)}...
            </p>
            
            <div className="space-y-5">
              {/* Sélection du site WordPress */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🌐 Site WordPress de destination *
                </label>
                <select
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent text-base"
                  required
                >
                  {wordpressSites.length === 0 ? (
                    <option value="">Aucun site configuré</option>
                  ) : (
                    wordpressSites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.site_name} ({site.site_url})
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Aperçu du branding du site sélectionné */}
              {selectedSite && wordpressSites.find(s => s.id === selectedSite) && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">🎨 Branding qui sera appliqué</h4>
                  {(() => {
                    const site = wordpressSites.find(s => s.id === selectedSite);
                    return (
                      <div className="space-y-3">
                        {/* Couleurs */}
                        <div className="flex items-center space-x-3">
                          <span className="text-xs text-gray-500 w-20">Couleurs :</span>
                          <div 
                            className="w-8 h-8 rounded-lg border-2 border-white shadow-sm" 
                            style={{ backgroundColor: site.company_colors?.primary || '#4F86EF' }}
                            title={`Primaire: ${site.company_colors?.primary}`}
                          ></div>
                          <div 
                            className="w-8 h-8 rounded-lg border-2 border-white shadow-sm" 
                            style={{ backgroundColor: site.company_colors?.secondary || '#3A6B35' }}
                            title={`Secondaire: ${site.company_colors?.secondary}`}
                          ></div>
                        </div>
                        
                        {/* CTA URL */}
                        <div className="flex items-start space-x-3">
                          <span className="text-xs text-gray-500 w-20">CTA vers :</span>
                          <span className="text-xs text-[#4F86EF] break-all">{site.cta_url || 'Non défini'}</span>
                        </div>
                        
                        {/* Ton */}
                        <div className="flex items-center space-x-3">
                          <span className="text-xs text-gray-500 w-20">Ton :</span>
                          <span className="text-xs font-medium text-gray-700 capitalize">{site.cta_tone || 'Professionnel'}</span>
                        </div>
                        
                        {/* Description */}
                        {site.company_description && (
                          <div className="flex items-start space-x-3">
                            <span className="text-xs text-gray-500 w-20">Activité :</span>
                            <span className="text-xs text-gray-600 line-clamp-2">{site.company_description}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Mots-clés SEO */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🔑 Mots-clés SEO (optionnel)
                </label>
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="prix, tarif, devis, installation"
                  data-testid="keywords-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Séparez les mots-clés par des virgules (4-6 recommandés)
                </p>
              </div>

              {/* Boutons d'action */}
              <div className="flex space-x-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setShowKeywordsModal(false);
                    setKeywords("");
                    setSelectedEntry(null);
                  }}
                  className="btn-secondary flex-1"
                  data-testid="cancel-generate"
                >
                  Annuler
                </button>
                <button
                  onClick={handleGenerateArticle}
                  disabled={!selectedSite}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-generate"
                >
                  ✨ Générer l'article
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
